import { z } from 'zod';
import { timesheetsMileageAccuracyMillimetersSchema, timesheetsMileageLatitudeE7Schema, timesheetsMileageLongitudeE7Schema, } from './timesheets-mileage.contract.js';
/**
 * Public Timesheets boundary for clocked-in location evidence. Coordinates are retained evidence;
 * this contract never authorizes a payroll boundary or mutates a time entry by itself.
 */
export const timesheetsLocationEvidenceCapability = 'timesheets.location-evidence.v1';
export const timesheetsLocationBoundaryAuthority = 'supporting-evidence-only';
export const timesheetsLocationDevicePlatformSchema = z.enum(['ios', 'android', 'web']);
export const timesheetsLocationDeviceTrustSchema = z.enum(['registered', 'attested']);
export const timesheetsLocationDeviceStatusSchema = z.enum(['active', 'revoked']);
export const timesheetsLocationDeviceSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    clientDeviceId: z.string().uuid(),
    platform: timesheetsLocationDevicePlatformSchema,
    displayLabel: z.string().trim().min(1).max(120).nullable(),
    appVersion: z.string().trim().min(1).max(80).nullable(),
    trust: timesheetsLocationDeviceTrustSchema,
    status: timesheetsLocationDeviceStatusSchema,
    attestedAt: z.string().datetime({ offset: true }).nullable(),
    revokedAt: z.string().datetime({ offset: true }).nullable(),
    revokedReason: z.string().trim().min(5).max(1_000).nullable(),
    revision: z.number().int().positive(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((device, context) => {
    if ((device.trust === 'attested') !== (device.attestedAt !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['attestedAt'],
            message: 'Only an attested device may carry an attestation timestamp.',
        });
    }
    if ((device.status === 'revoked') !== (device.revokedAt !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['revokedAt'],
            message: 'A revoked device must retain its revocation timestamp.',
        });
    }
});
export const timesheetsLocationRegisterDeviceSchema = z.strictObject({
    commandId: z.string().uuid(),
    clientDeviceId: z.string().uuid(),
    platform: timesheetsLocationDevicePlatformSchema,
    displayLabel: z.string().trim().min(1).max(120).nullable().default(null),
    appVersion: z.string().trim().min(1).max(80).nullable().default(null),
});
export const timesheetsLocationRevokeDeviceSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(5).max(1_000),
});
export const timesheetsLocationPointSourceSchema = z.enum([
    'clock-boundary',
    'periodic',
    'geofence-transition',
    'offline-replay',
]);
export const timesheetsLocationCaptureModeSchema = z.enum([
    'foreground',
    'background',
    'region-transition',
    'offline-replay',
]);
export const timesheetsLocationSignalQualitySchema = z.enum([
    'strong',
    'usable',
    'low',
    'missing',
]);
export const timesheetsLocationAutomaticTrustSchema = z.enum([
    'eligible',
    'review-required',
    'excluded',
]);
export const timesheetsLocationReviewStatusSchema = z.enum([
    'not-required',
    'pending',
    'accepted',
    'excluded',
]);
export const timesheetsLocationReasonSchema = z.enum([
    'device-not-attested',
    'accuracy-missing',
    'accuracy-low',
    'offline-replay',
    'receipt-delayed',
    'receipt-expired',
    'occurred-in-future',
    'outside-entry-window',
    'entry-needs-correction',
    'out-of-order',
    'impossible-path',
    'managed-place-overlap',
    'project-match-ambiguous',
    'project-location-unavailable',
    'dwell-insufficient',
    'dwell-gap-too-large',
    'latest-point-stale',
]);
export const timesheetsManagedPlaceKindSchema = z.enum([
    'shop',
    'warehouse',
    'vendor',
    'material-supplier',
    'other',
]);
export const timesheetsManagedPlaceSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    kind: timesheetsManagedPlaceKindSchema,
    label: z.string().trim().min(1).max(240),
    latitudeE7: timesheetsMileageLatitudeE7Schema,
    longitudeE7: timesheetsMileageLongitudeE7Schema,
    radiusMillimeters: z.number().int().min(10_000).max(1_000_000),
    active: z.boolean(),
    revision: z.number().int().positive(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const timesheetsManagedPlaceCreateSchema = z.strictObject({
    commandId: z.string().uuid(),
    kind: timesheetsManagedPlaceKindSchema,
    label: z.string().trim().min(1).max(240),
    latitudeE7: timesheetsMileageLatitudeE7Schema,
    longitudeE7: timesheetsMileageLongitudeE7Schema,
    radiusMillimeters: z.number().int().min(10_000).max(1_000_000),
});
export const timesheetsManagedPlaceUpdateSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    kind: timesheetsManagedPlaceKindSchema.optional(),
    label: z.string().trim().min(1).max(240).optional(),
    latitudeE7: timesheetsMileageLatitudeE7Schema.optional(),
    longitudeE7: timesheetsMileageLongitudeE7Schema.optional(),
    radiusMillimeters: z.number().int().min(10_000).max(1_000_000).optional(),
    active: z.boolean().optional(),
}).refine((value) => Object.keys(value).some((key) => !['commandId', 'expectedRevision'].includes(key)), 'At least one managed-place field must be changed.');
export const timesheetsLocationRecordPointSchema = z.strictObject({
    clientPointId: z.string().uuid(),
    clientDeviceId: z.string().uuid(),
    timeEntryId: z.string().uuid(),
    occurredAt: z.string().datetime({ offset: true }),
    latitudeE7: timesheetsMileageLatitudeE7Schema,
    longitudeE7: timesheetsMileageLongitudeE7Schema,
    accuracyMillimeters: timesheetsMileageAccuracyMillimetersSchema.nullable().default(null),
    source: timesheetsLocationPointSourceSchema,
    captureMode: timesheetsLocationCaptureModeSchema,
    deviceSequence: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).nullable().default(null),
});
export const timesheetsLocationPointSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    timeEntryId: z.string().uuid(),
    deviceId: z.string().uuid(),
    clientPointId: z.string().uuid(),
    deviceSequence: z.number().int().nonnegative().nullable(),
    occurredAt: z.string().datetime({ offset: true }),
    receivedAt: z.string().datetime({ offset: true }),
    latitudeE7: timesheetsMileageLatitudeE7Schema,
    longitudeE7: timesheetsMileageLongitudeE7Schema,
    accuracyMillimeters: timesheetsMileageAccuracyMillimetersSchema.nullable(),
    source: timesheetsLocationPointSourceSchema,
    captureMode: timesheetsLocationCaptureModeSchema,
    deviceTrust: timesheetsLocationDeviceTrustSchema,
    signalQuality: timesheetsLocationSignalQualitySchema,
    receiptDelaySeconds: z.number().int().min(-86_400).max(2_592_000),
    previousPointId: z.string().uuid().nullable(),
    distanceFromPreviousMillimeters: z.number().int().nonnegative().max(100_000_000_000),
    elapsedFromPreviousSeconds: z.number().int().nullable(),
    speedMillimetersPerSecond: z.number().int().nonnegative().max(100_000_000).nullable(),
    currentWorkReferenceId: z.string().uuid().nullable(),
    managedPlaceId: z.string().uuid().nullable(),
    managedPlaceKind: timesheetsManagedPlaceKindSchema.nullable(),
    managedPlaceMatchIds: z.array(z.string().uuid()).max(25),
    projectMatchIds: z.array(z.string().uuid()).max(25),
    projectLocationAvailable: z.boolean(),
    automaticTrust: timesheetsLocationAutomaticTrustSchema,
    trustReasons: z.array(timesheetsLocationReasonSchema).max(18),
    reviewStatus: timesheetsLocationReviewStatusSchema,
    reviewRevision: z.number().int().positive(),
    boundaryAuthority: z.literal(timesheetsLocationBoundaryAuthority),
    createdAt: z.string().datetime({ offset: true }),
});
export const timesheetsLocationReviewDecisionSchema = z.enum([
    'accept-supporting-evidence',
    'exclude',
]);
export const timesheetsLocationReviewPointSchema = z.strictObject({
    commandId: z.string().uuid(),
    expectedReviewRevision: z.number().int().positive(),
    decision: timesheetsLocationReviewDecisionSchema,
    reason: z.string().trim().min(5).max(2_000),
});
export const timesheetsLocationReviewTransitionSchema = z.strictObject({
    id: z.string().uuid(),
    pointId: z.string().uuid(),
    commandId: z.string().uuid(),
    previousStatus: timesheetsLocationReviewStatusSchema,
    nextStatus: z.enum(['accepted', 'excluded']),
    reviewRevision: z.number().int().positive(),
    decision: timesheetsLocationReviewDecisionSchema,
    reason: z.string().trim().min(5).max(2_000),
    reviewedByMembershipId: z.string().uuid(),
    reviewedAt: z.string().datetime({ offset: true }),
    boundaryAuthority: z.literal(timesheetsLocationBoundaryAuthority),
});
export const timesheetsLocationVisitCandidateSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    membershipId: z.string().uuid(),
    timeEntryId: z.string().uuid(),
    projectId: z.string().uuid(),
    observedFrom: z.string().datetime({ offset: true }),
    observedThrough: z.string().datetime({ offset: true }),
    pointCount: z.number().int().min(2).max(1_000),
    status: z.enum(['pending', 'accepted', 'excluded']),
    reviewRevision: z.number().int().positive(),
    boundaryAuthority: z.literal(timesheetsLocationBoundaryAuthority),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).refine((candidate) => candidate.observedThrough >= candidate.observedFrom, {
    path: ['observedThrough'],
    message: 'Location visit evidence cannot end before it begins.',
});
export const timesheetsLocationVisitReviewTransitionSchema = z.strictObject({
    id: z.string().uuid(),
    visitCandidateId: z.string().uuid(),
    commandId: z.string().uuid(),
    previousStatus: z.enum(['pending', 'accepted', 'excluded']),
    nextStatus: z.enum(['accepted', 'excluded']),
    reviewRevision: z.number().int().positive(),
    decision: timesheetsLocationReviewDecisionSchema,
    reason: z.string().trim().min(5).max(2_000),
    reviewedByMembershipId: z.string().uuid(),
    reviewedAt: z.string().datetime({ offset: true }),
    boundaryAuthority: z.literal(timesheetsLocationBoundaryAuthority),
});
export const timesheetsLocationListQuerySchema = z.strictObject({
    scope: z.enum(['own', 'all']).default('own'),
    membershipId: z.string().uuid().optional(),
    timeEntryId: z.string().uuid().optional(),
    reviewStatus: timesheetsLocationReviewStatusSchema.optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    limit: z.number().int().min(1).max(500).default(100),
}).superRefine((query, context) => {
    if (query.scope === 'own' && query.membershipId !== undefined) {
        context.addIssue({
            code: 'custom',
            path: ['membershipId'],
            message: 'Own location queries cannot select another membership.',
        });
    }
    if (query.from !== undefined && query.to !== undefined && query.to < query.from) {
        context.addIssue({
            code: 'custom',
            path: ['to'],
            message: 'Location range end must not precede its start.',
        });
    }
});
export const timesheetsLocationVisitListQuerySchema = z.strictObject({
    scope: z.enum(['own', 'all']).default('own'),
    membershipId: z.string().uuid().optional(),
    timeEntryId: z.string().uuid().optional(),
    status: z.enum(['pending', 'accepted', 'excluded']).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    limit: z.number().int().min(1).max(500).default(100),
}).superRefine((query, context) => {
    if (query.scope === 'own' && query.membershipId !== undefined) {
        context.addIssue({
            code: 'custom',
            path: ['membershipId'],
            message: 'Own visit queries cannot select another membership.',
        });
    }
    if (query.from !== undefined && query.to !== undefined && query.to < query.from) {
        context.addIssue({
            code: 'custom',
            path: ['to'],
            message: 'Visit range end must not precede its start.',
        });
    }
});
export const timesheetsManagedPlaceListQuerySchema = z.strictObject({
    includeInactive: z.boolean().default(false),
});
export const timesheetsLocationDeviceResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: timesheetsLocationDeviceSchema,
    idempotent: z.boolean(),
});
export const timesheetsLocationPointResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: z.strictObject({
        point: timesheetsLocationPointSchema,
        visitCandidate: timesheetsLocationVisitCandidateSchema.nullable(),
    }),
    idempotent: z.boolean(),
});
export const timesheetsLocationReviewResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: timesheetsLocationPointSchema,
    transition: timesheetsLocationReviewTransitionSchema,
    idempotent: z.boolean(),
});
export const timesheetsLocationListResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: z.array(timesheetsLocationPointSchema),
});
export const timesheetsLocationDeviceListResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: z.array(timesheetsLocationDeviceSchema),
});
export const timesheetsLocationVisitListResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: z.array(timesheetsLocationVisitCandidateSchema),
});
export const timesheetsLocationVisitReviewResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: timesheetsLocationVisitCandidateSchema,
    transition: timesheetsLocationVisitReviewTransitionSchema,
    idempotent: z.boolean(),
});
export const timesheetsManagedPlaceResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: timesheetsManagedPlaceSchema,
    idempotent: z.boolean(),
});
export const timesheetsManagedPlaceListResponseSchema = z.strictObject({
    contractVersion: z.literal(timesheetsLocationEvidenceCapability),
    data: z.array(timesheetsManagedPlaceSchema),
});
//# sourceMappingURL=timesheets-location-evidence.contract.js.map